import { MapPin } from "lucide-react";
import { motion } from "framer-motion";
import { Rise } from "./Reveal";
import { StoreButtons } from "./StoreButtons";
import { GetAppLink } from "./GetAppLink";
import { CONTACT } from "../../content";
import { useT } from "../../i18n/LangContext";

const Tile = ({ src, alt, className = "" }) => (
  <div className={`overflow-hidden rounded-xl bg-[#F3F3F3] ${className}`}>
    <img
      src={src}
      alt={alt}
      loading="eager"
      className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
    />
  </div>
);

const Collage = ({ t, getApp }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
    className="grid auto-rows-[112px] grid-cols-2 gap-3 sm:auto-rows-[150px]"
    data-testid="hero-collage"
  >
    <Tile src="/hero-coast.webp" alt={t.imgAlt} className="row-span-2" />
    <Tile src="/airport.webp" alt={t.collage.airport} />
    <div className="flex flex-col justify-between rounded-xl bg-[#F3F3F3] p-4 sm:p-5" data-testid="collage-cta-tile">
      <p className="text-base font-bold leading-tight text-black sm:text-2xl">{t.collage.ready}</p>
      <GetAppLink
        className="w-fit whitespace-nowrap rounded-full bg-black px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-zinc-800 sm:px-4 sm:py-2 sm:text-sm"
        testid="collage-get-app-btn"
      >
        {getApp}
      </GetAppLink>
    </div>
    <Tile src="/car.webp" alt={t.collage.car} />
    <Tile src="/town.webp" alt={t.collage.town} />
  </motion.div>
);

export const Hero = () => {
  const t = useT();
  const h = t.hero;
  return (
    <section id="top" className="bg-white px-5 pb-16 pt-8 sm:px-8 lg:pb-24 lg:pt-16" data-testid="hero-section">
      <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2 lg:gap-16">
        <div id="download" className="scroll-mt-24">
          <Rise>
            <p className="flex items-center gap-2 text-sm font-medium text-black" data-testid="hero-location">
              <MapPin className="h-4 w-4 text-[#FFD000]" /> {h.location}
            </p>
          </Rise>
          <Rise delay={0.05}>
            <h1 className="mt-5 text-[36px] font-bold leading-[1.1] tracking-[-0.02em] text-black lg:text-[52px]" data-testid="hero-headline">
              {h.title}
            </h1>
          </Rise>
          <Rise delay={0.1}>
            <p className="mt-5 max-w-md text-base text-zinc-600 lg:text-lg" data-testid="hero-subtitle">{h.text}</p>
          </Rise>
          <Rise delay={0.15} className="mt-8">
            <StoreButtons />
          </Rise>
          <Rise delay={0.2}>
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-block text-base font-medium text-black underline underline-offset-4 transition-colors hover:text-zinc-600"
              data-testid="hero-whatsapp-link"
            >
              {h.whatsappLink}
            </a>
          </Rise>
        </div>
        <Collage t={h} getApp={t.getApp} />
      </div>
    </section>
  );
};
